import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, History, Radio } from "lucide-react";
import { toast } from "sonner";

const fmt = (d) => (d ? new Date(d.length === 10 ? `${d}T00:00:00` : d).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "—");

function Rows({ items, offset }) {
  if (!items.length) return <p className="text-sm text-slate-500 p-6 text-center" data-testid="metric-list-empty">No records for this count.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="metric-list-table">
        <thead><tr className="text-left text-[11px] uppercase text-slate-500 border-b border-slate-200">{["#", "Person", "Program", "Stage", "Recruiter", "Date", "Detail"].map((h) => <th key={h} className="py-2 px-3">{h}</th>)}</tr></thead>
        <tbody>{items.map((r, i) => (
          <tr key={`${r.pid}-${i}`} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`metric-list-row-${offset + i}`}>
            <td className="py-2 px-3 text-slate-400">{offset + i + 1}</td>
            <td className="py-2 px-3"><Link to={`/people/detail/${r.pid}`} className="font-medium text-brand hover:underline">{r.name}</Link></td>
            <td className="py-2 px-3 text-slate-600">{r.program}</td><td className="py-2 px-3 text-slate-600">{r.stage}</td>
            <td className="py-2 px-3 text-slate-600">{r.recruiter}</td><td className="py-2 px-3 text-slate-600 whitespace-nowrap">{fmt(r.date)}</td>
            <td className="py-2 px-3 text-slate-500 text-xs">{r.detail || ""}</td>
          </tr>))}</tbody>
      </table>
    </div>
  );
}

export default function MetricList() {
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const page = Number(sp.get("page") || 1);
  const rid = sp.get("rid");
  useEffect(() => {
    setData(null);
    const params = Object.fromEntries(sp.entries());
    delete params.from_rid;
    const req = rid ? api.get(`/my-brief/reports/${rid}/records`, { params: { metric: params.metric, page } }) : api.get("/recruiting/metrics/list", { params: { ...params, page } });
    req.then((r) => setData(r.data)).catch((e) => { toast.error(apiError(e)); setData({ error: true }); });
  }, [sp, rid, page]);
  const go = (p) => { const n = new URLSearchParams(sp); n.set("page", String(p)); setSp(n); };
  const openLive = () => setSp(new URLSearchParams({ ...data.live_params, metric: data.metric, from_rid: rid }));
  const backToSnap = () => setSp(new URLSearchParams({ rid: sp.get("from_rid"), metric: sp.get("metric") }));
  if (!data) return <div className="h-60 rounded-xl bg-slate-50 animate-pulse" />;
  if (data.error) return <p className="text-sm text-slate-500 p-6" data-testid="metric-list-error">This list isn't available to you.</p>;
  const size = 50;
  return (
    <div>
      <button onClick={() => navigate(-1)} className="text-xs text-brand font-medium mb-2 flex items-center gap-1" data-testid="metric-list-back"><ChevronLeft className="w-3.5 h-3.5" />Back</button>
      <PageHeader title={`${data.label} · ${data.total}`} description={data.definition} />
      <div className={`rounded-xl border p-3 mb-3 text-sm flex flex-wrap items-center gap-2 ${data.live ? "border-sky-200 bg-sky-50 text-sky-900" : "border-amber-200 bg-amber-50 text-amber-900"}`} data-testid="metric-list-mode">
        {data.live ? <Radio className="w-4 h-4" /> : <History className="w-4 h-4" />}
        <span className="flex-1">{data.live
          ? <>Live list as of {new Date(data.as_of).toLocaleString()} · {data.scope_label}. Data may have changed since any saved report.</>
          : <>Saved report snapshot as of {new Date(data.as_of).toLocaleString()} — this is the exact record list behind the report's count. Live data may have changed since.</>}</span>
        {!data.live && <Button size="sm" variant="outline" onClick={openLive} data-testid="metric-list-live">Compare with live list (same filters)</Button>}
        {data.live && sp.get("from_rid") && <Button size="sm" variant="outline" onClick={backToSnap} data-testid="metric-list-snapshot">Back to saved snapshot</Button>}
      </div>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <Rows items={data.items} offset={(page - 1) * size} />
        <div className="flex items-center justify-between p-3 text-xs text-slate-500" data-testid="metric-list-pager">
          <span data-testid="metric-list-range">Showing {data.total ? (page - 1) * size + 1 : 0}–{Math.min(page * size, data.total)} of {data.total}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => go(page - 1)} data-testid="metric-list-prev"><ChevronLeft className="w-4 h-4" /></Button>
            <span className="self-center">Page {page} of {data.pages}</span>
            <Button size="sm" variant="outline" disabled={page >= data.pages} onClick={() => go(page + 1)} data-testid="metric-list-next"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      </div>
    </div>
  );
}
